import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useRealtime } from '../contexts/RealtimeContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Bell, CreditCard, Users, Vote, Car, FileText, 
  AlertTriangle, Check, CheckCheck, Trash2, X,
  ArrowLeft, Loader2, Inbox, UserPlus, Wifi, WifiOff
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { toast } from 'sonner';

const notificationIcons = {
  payment_created: CreditCard,
  payment_received: CreditCard,
  payment_reminder: CreditCard,
  payment_overdue: AlertTriangle,
  member_joined: Users,
  member_left: Users,
  vote_created: Vote,
  vote_ended: Vote,
  withdrawal_request: AlertTriangle,
  withdrawal_approved: Check,
  withdrawal_rejected: X,
  carpool_created: Car,
  carpool_request: Car,
  carpool_accepted: Car,
  file_uploaded: FileText,
  announcement: Bell,
  system: Bell,
  reminder_payment: CreditCard,
  reminder_vote: Vote,
  reminder_custom: Bell,
  trip_updated: Bell,
  join_request: UserPlus,
  join_approved: Check,
  join_rejected: X,
};

const notificationColors = {
  payment_overdue: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  withdrawal_request: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  withdrawal_rejected: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  withdrawal_approved: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  payment_received: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  member_joined: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  vote_created: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  join_request: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  join_approved: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  join_rejected: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  default: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

export default function Notifications() {
  const { api, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [processingIds, setProcessingIds] = useState(new Set());

  // Realtime connection status
  const { isConnected: sseConnected, unreadCount: realtimeUnreadCount } = useRealtime();

  const dateLocale = language === 'pt-BR' ? ptBR : enUS;

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const response = await api.get('/notifications?limit=100');
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error(t('notifications.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated, t]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Refresh when new realtime notifications arrive
  useEffect(() => {
    if (realtimeUnreadCount > 0) {
      let isMounted = true;
      const refreshNotifications = async () => {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (isMounted) {
          fetchNotifications();
        }
      };
      refreshNotifications();
      return () => { isMounted = false; };
    }
  }, [realtimeUnreadCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success(t('notifications.markedAllRead'));
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error(t('notifications.markError'));
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(notifications.filter(n => n.id !== notificationId));
      toast.success(t('notifications.deleted'));
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error(t('notifications.deleteError'));
    }
  };

  const handleClearAll = async () => {
    try {
      await api.delete('/notifications');
      setNotifications([]);
      toast.success(t('notifications.clearedAll'));
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast.error(t('notifications.clearError'));
    }
  };

  const handleApproveRequest = async (notification, e) => {
    e.stopPropagation();
    
    const { trip_id, member_id, requester_id } = notification.data || {};
    
    if (!trip_id || (!member_id && !requester_id)) {
      toast.error(t('notifications.joinRequest.approveError'));
      return;
    }

    setProcessingIds(prev => new Set(prev).add(notification.id));
    
    try {
      // If we have member_id, use it directly. Otherwise, fetch pending requests
      let targetMemberId = member_id;
      
      if (!targetMemberId && requester_id) {
        // Fetch pending requests to find the member_id
        const pendingRes = await api.get(`/trips/${trip_id}/pending-requests`);
        const pendingMember = pendingRes.data?.find(m => m.user_id === requester_id);
        if (pendingMember) {
          targetMemberId = pendingMember.id;
        }
      }

      if (!targetMemberId) {
        // No pending request found - it may have been already processed
        // Remove the stale notification
        setNotifications(notifications.filter(n => n.id !== notification.id));
        await api.delete(`/notifications/${notification.id}`).catch(() => {});
        toast.info(language === 'pt-BR' 
          ? 'Esta solicitação já foi processada' 
          : 'This request has already been processed');
        return;
      }

      await api.post(`/trips/${trip_id}/members/${targetMemberId}/approve`);
      
      // Remove the notification from list after successful action
      setNotifications(notifications.filter(n => n.id !== notification.id));
      toast.success(t('notifications.joinRequest.approved'));
    } catch (error) {
      console.error('Error approving request:', error);
      
      // Check if the error is "already processed"
      const errorMsg = error.response?.data?.detail || '';
      if (errorMsg.includes('já foi processada') || errorMsg.includes('already processed') || error.response?.status === 400) {
        // Remove stale notification
        setNotifications(notifications.filter(n => n.id !== notification.id));
        await api.delete(`/notifications/${notification.id}`).catch(() => {});
        toast.info(language === 'pt-BR' 
          ? 'Esta solicitação já foi processada' 
          : 'This request has already been processed');
      } else {
        toast.error(t('notifications.joinRequest.approveError'));
      }
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notification.id);
        return newSet;
      });
    }
  };

  const handleRejectRequest = async (notification, e) => {
    e.stopPropagation();
    
    const { trip_id, member_id, requester_id } = notification.data || {};
    
    if (!trip_id || (!member_id && !requester_id)) {
      toast.error(t('notifications.joinRequest.rejectError'));
      return;
    }

    setProcessingIds(prev => new Set(prev).add(notification.id));
    
    try {
      // If we have member_id, use it directly. Otherwise, fetch pending requests
      let targetMemberId = member_id;
      
      if (!targetMemberId && requester_id) {
        // Fetch pending requests to find the member_id
        const pendingRes = await api.get(`/trips/${trip_id}/pending-requests`);
        const pendingMember = pendingRes.data?.find(m => m.user_id === requester_id);
        if (pendingMember) {
          targetMemberId = pendingMember.id;
        }
      }

      if (!targetMemberId) {
        // No pending request found - it may have been already processed
        // Remove the stale notification
        setNotifications(notifications.filter(n => n.id !== notification.id));
        await api.delete(`/notifications/${notification.id}`).catch(() => {});
        toast.info(language === 'pt-BR' 
          ? 'Esta solicitação já foi processada' 
          : 'This request has already been processed');
        return;
      }

      await api.post(`/trips/${trip_id}/members/${targetMemberId}/reject`);
      
      // Remove the notification from list after successful action
      setNotifications(notifications.filter(n => n.id !== notification.id));
      toast.success(t('notifications.joinRequest.rejected'));
    } catch (error) {
      console.error('Error rejecting request:', error);
      
      // Check if the error is "already processed"
      const errorMsg = error.response?.data?.detail || '';
      if (errorMsg.includes('já foi processada') || errorMsg.includes('already processed') || error.response?.status === 400) {
        // Remove stale notification
        setNotifications(notifications.filter(n => n.id !== notification.id));
        await api.delete(`/notifications/${notification.id}`).catch(() => {});
        toast.info(language === 'pt-BR' 
          ? 'Esta solicitação já foi processada' 
          : 'This request has already been processed');
      } else {
        toast.error(t('notifications.joinRequest.rejectError'));
      }
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notification.id);
        return newSet;
      });
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }
    
    if (notification.trip_id || notification.data?.trip_id) {
      const tripId = notification.trip_id || notification.data?.trip_id;
      const type = notification.type || '';
      
      // Route based on notification type
      if (type.includes('payment') || type === 'reminder_payment') {
        navigate(`/trip/${tripId}/payments`);
      } else if (type === 'reminder_vote' || (type.includes('vote') && !type.includes('reminder'))) {
        navigate(`/trip/${tripId}/votes`);
      } else if (type.includes('withdrawal')) {
        navigate(`/trip/${tripId}/members`);
      } else if (type.includes('carpool')) {
        navigate(`/trip/${tripId}/carpools`);
      } else if (type.includes('file')) {
        navigate(`/trip/${tripId}/files`);
      } else if (type === 'join_approved') {
        navigate(`/trip/${tripId}`);
      } else if (type === 'join_request' || type === 'join_rejected') {
        // Join requests and rejections go to members page
        navigate(`/trip/${tripId}/members`);
      } else if (type.includes('reminder') || type.includes('task')) {
        // Generic reminders and tasks go to tasks page
        navigate(`/trip/${tripId}/tasks`);
      } else {
        navigate(`/trip/${tripId}`);
      }
    }
  };

  const getIcon = (type) => {
    const Icon = notificationIcons[type] || Bell;
    return Icon;
  };

  const getIconColor = (type) => {
    return notificationColors[type] || notificationColors.default;
  };

  // Get notification category label based on type
  const getNotificationCategory = (type) => {
    if (!type) return t('notifications.category.general');
    
    if (type.includes('payment') || type === 'reminder_payment') {
      return t('notifications.category.payment');
    }
    if (type.includes('member') || type === 'join_request' || type === 'join_approved' || type === 'join_rejected') {
      return t('notifications.category.member');
    }
    if (type.includes('vote') || type === 'reminder_vote') {
      return t('notifications.category.vote');
    }
    if (type.includes('reminder') || type.includes('task')) {
      return t('notifications.category.reminder');
    }
    if (type.includes('carpool')) {
      return t('notifications.category.carpool');
    }
    if (type.includes('withdrawal')) {
      return t('notifications.category.withdrawal');
    }
    if (type.includes('file')) {
      return t('notifications.category.file');
    }
    return t('notifications.category.general');
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: dateLocale });
    } catch {
      return '';
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (language === 'pt-BR') {
        return format(date, "d 'de' MMMM 'às' HH:mm", { locale: dateLocale });
      }
      return format(date, "MMMM d 'at' HH:mm", { locale: dateLocale });
    } catch {
      return '';
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  
  const filteredNotifications = activeTab === 'unread' 
    ? notifications.filter(n => !n.read)
    : notifications;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {t('notifications.title')}
                </h1>
                {/* SSE Connection indicator */}
                {sseConnected ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400" title={language === 'pt-BR' ? 'Tempo real ativo' : 'Realtime active'}>
                    <Wifi className="w-3 h-3" />
                    <span className="hidden sm:inline">{language === 'pt-BR' ? 'Tempo real' : 'Realtime'}</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400" title={language === 'pt-BR' ? 'Reconectando...' : 'Reconnecting...'}>
                    <WifiOff className="w-3 h-3" />
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {unreadCount > 0 
                  ? `${unreadCount} ${unreadCount > 1 ? t('notifications.unreadCountPlural') : t('notifications.unreadCount')}`
                  : t('notifications.allReadMessage')
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleMarkAllAsRead}
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                {t('notifications.markAllRead')}
              </Button>
            )}
            {notifications.length > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleClearAll}
                className="text-red-600 hover:text-red-700"
                data-testid="clear-all-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('notifications.clearAll')}
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">
              {t('notifications.all')} ({notifications.length})
            </TabsTrigger>
            <TabsTrigger value="unread" data-testid="tab-unread">
              {t('notifications.unread')} ({unreadCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Notifications List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Inbox className="w-16 h-16 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                  {activeTab === 'unread' 
                    ? t('notifications.noUnread')
                    : t('notifications.noNotifications')
                  }
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  {activeTab === 'unread'
                    ? t('notifications.allRead')
                    : t('notifications.empty')
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredNotifications.map((notification, index) => {
              const Icon = getIcon(notification.type);
              const iconColorClass = getIconColor(notification.type);
              const isJoinRequest = notification.type === 'join_request';
              const isProcessing = processingIds.has(notification.id);
              
              return (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className={`transition-all hover:shadow-md ${
                      !notification.read 
                        ? 'border-l-4 border-l-orange-500 bg-orange-50/50 dark:bg-orange-950/20' 
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    } ${isJoinRequest ? 'cursor-default' : 'cursor-pointer'}`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={`p-2 rounded-lg ${iconColorClass}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              {/* Category Badge */}
                              <span className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wide">
                                {t('notifications.title')}: {getNotificationCategory(notification.type)}
                              </span>
                              <h3 className={`text-sm font-medium text-slate-900 dark:text-white mt-0.5 ${
                                !notification.read ? 'font-semibold' : ''
                              }`}>
                                {notification.title}
                              </h3>
                              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                                {notification.message}
                              </p>
                              
                              {/* Join Request Actions */}
                              {isJoinRequest && (
                                <div className="flex items-center gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    onClick={(e) => handleApproveRequest(notification, e)}
                                    disabled={isProcessing}
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    data-testid={`approve-request-${notification.id}`}
                                  >
                                    {isProcessing ? (
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    ) : (
                                      <Check className="w-4 h-4 mr-1" />
                                    )}
                                    {isProcessing ? t('notifications.joinRequest.processing') : t('notifications.joinRequest.approve')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => handleRejectRequest(notification, e)}
                                    disabled={isProcessing}
                                    className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-950"
                                    data-testid={`reject-request-${notification.id}`}
                                  >
                                    {isProcessing ? (
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    ) : (
                                      <X className="w-4 h-4 mr-1" />
                                    )}
                                    {isProcessing ? t('notifications.joinRequest.processing') : t('notifications.joinRequest.reject')}
                                  </Button>
                                </div>
                              )}
                              
                              <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                                {formatDate(notification.created_at)} • {formatTime(notification.created_at)}
                              </p>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-1">
                              {!notification.read && (
                                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-400 hover:text-red-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(notification.id);
                                }}
                                data-testid={`delete-notification-${notification.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
